import { useState } from "react";
import { X, Copy, Check, Loader2, Globe, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ShareModal({ design, onClose, onUpdate }) {
  const [token, setToken] = useState(design.share_token || null);
  const [isPublic, setIsPublic] = useState(!!design.is_public);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const publicUrl = token ? `${window.location.origin}/look/${token}` : "";

  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/designs/${design.id}/share`);
      setToken(data.share_token);
      setIsPublic(true);
      onUpdate && onUpdate({ ...design, share_token: data.share_token, is_public: true });
      toast.success("Public link ready");
    } catch (err) {
      console.error(err);
      toast.error("Failed to create link");
    } finally { setBusy(false); }
  };

  const unshare = async () => {
    setBusy(true);
    try {
      await api.post(`/designs/${design.id}/unshare`);
      setIsPublic(false);
      onUpdate && onUpdate({ ...design, is_public: false });
      toast.success("Link disabled");
    } catch (err) {
      console.error(err);
      toast.error("Failed");
    } finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-zinc-950/90 backdrop-blur-md flex items-center justify-center px-4" data-testid="share-modal">
      <div className="w-full max-w-lg bg-zinc-50 text-zinc-950 border border-zinc-200 relative p-8">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 bg-zinc-950 text-zinc-50 hover:bg-lime-400 hover:text-zinc-950 flex items-center justify-center" data-testid="share-close-btn">
          <X className="w-4 h-4" />
        </button>
        <div className="overline text-zinc-500 mb-2">Share this look</div>
        <h3 className="font-serif text-4xl leading-tight">{design.title}</h3>
        <p className="mt-3 text-sm text-zinc-600 leading-relaxed">A public link anyone can open — no sign-in required. Perfect for buyers, press, or a moodboard DM.</p>

        {isPublic && token ? (
          <div className="mt-6" data-testid="share-live-view">
            <div className="flex items-center gap-2 border border-zinc-950 bg-white p-2">
              <Globe className="w-4 h-4 text-lime-600 shrink-0 ml-1" />
              <input readOnly value={publicUrl} className="flex-1 bg-transparent px-2 py-1 text-sm font-mono outline-none" data-testid="share-url-input" />
              <button onClick={copy} className="px-3 py-2 bg-zinc-950 text-zinc-50 overline hover:bg-lime-400 hover:text-zinc-950 flex items-center gap-1" data-testid="share-copy-btn">
                {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
            <div className="mt-4 flex gap-2">
              <a href={publicUrl} target="_blank" rel="noreferrer" className="flex-1 py-3 border border-zinc-950 overline text-center hover:bg-zinc-950 hover:text-zinc-50 transition-colors" data-testid="share-open-btn">Open in new tab ↗</a>
              <button onClick={unshare} disabled={busy} className="py-3 px-4 bg-white border border-rose-500 text-rose-600 overline hover:bg-rose-500 hover:text-white flex items-center gap-2 transition-colors" data-testid="share-disable-btn">
                <EyeOff className="w-3.5 h-3.5" /> Disable
              </button>
            </div>
          </div>
        ) : (
          <button onClick={share} disabled={busy} className={`mt-8 w-full py-4 rounded-full overline flex items-center justify-center gap-2 ${busy ? "bg-zinc-300 text-zinc-500" : "bg-lime-400 text-zinc-950 hover:bg-zinc-950 hover:text-lime-400"} transition-colors`} data-testid="share-enable-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing</> : <><Globe className="w-4 h-4" /> Create public link</>}
          </button>
        )}
      </div>
    </div>
  );
}
